import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useBills } from '../contexts/BillsContext';
import { BillStatus, BILL_STATUS_CONFIG } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';

const FILTER_STATUSES: BillStatus[] = ['DRAFT', 'APPROVED', 'PAID', 'VOIDED'];

interface BillFiltersProps {
  embedded?: boolean;
}

export function BillFilters({ embedded }: BillFiltersProps = {}) {
  const { billFilters, setBillFilters, fetchBills } = useBills();

  const [search, setSearch] = useState(billFilters.search || '');
  const [dateFrom, setDateFrom] = useState(billFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(billFilters.dateTo || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== (billFilters.search ?? '')) {
        setBillFilters({ search: search || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, billFilters.search, setBillFilters]);

  useEffect(() => {
    setDateFrom(billFilters.dateFrom || '');
    setDateTo(billFilters.dateTo || '');
  }, [billFilters.dateFrom, billFilters.dateTo]);

  useEffect(() => {
    // Apply one-sided OR two-sided ranges — both were previously required, so a
    // one-sided range was silently ignored (audit: medium).
    const t = setTimeout(() => {
      const from = dateFrom || undefined;
      const to = dateTo || undefined;
      if (from === (billFilters.dateFrom || undefined) && to === (billFilters.dateTo || undefined)) return;
      setBillFilters({ dateFrom: from, dateTo: to });
    }, 300);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo]);

  const statusArray = Array.isArray(billFilters.status) ? billFilters.status : (billFilters.status ? [billFilters.status] : []);
  const toggleStatus = (status: BillStatus) => {
    if (statusArray.includes(status)) {
      const next = statusArray.filter(s => s !== status);
      setBillFilters({ status: next.length === 0 ? undefined : next.length === 1 ? next[0] : next });
    } else {
      const next = [...statusArray, status];
      setBillFilters({ status: next.length === 1 ? next[0] : next });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setBillFilters({ status: undefined, search: '', contactId: undefined, dateFrom: undefined, dateTo: undefined });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
    setBillFilters({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters = statusArray.length > 0 || !!billFilters.search || !!billFilters.dateFrom || !!billFilters.dateTo;
  const hasDateRange = !!billFilters.dateFrom || !!billFilters.dateTo;
  const isMobile = useIsMobile(768);

  const getStatusLabel = () => {
    if (statusArray.length === 0) return 'All Statuses';
    if (statusArray.length === 1) return BILL_STATUS_CONFIG[statusArray[0]].label;
    return `${statusArray.length} selected`;
  };

  const filterRowDesktop = (
    <div className="accounting-filters-row d-flex align-items-center gap-3 flex-wrap">
      <div className="d-flex align-items-center position-relative flex-grow-1 accounting-filters-search" style={{ minWidth: '200px', maxWidth: '300px' }}>
        <i className="bi bi-search position-absolute ms-3 text-muted" />
        <input type="text" className="form-control form-control-sm ps-10" placeholder="Search bills, descriptions..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="dropdown">
        <button className="btn btn-sm btn-light dropdown-toggle d-flex align-items-center gap-2" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <i className="bi bi-filter" /> {getStatusLabel()}
        </button>
        <ul className="dropdown-menu py-2" style={{ minWidth: '180px' }}>
          <li>
            <label className="dropdown-item d-flex align-items-center py-2 mb-0" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input me-2 mt-0" checked={statusArray.length === 0} onChange={() => setBillFilters({ status: undefined })} />
              All Statuses
            </label>
          </li>
          <li><hr className="dropdown-divider" /></li>
          {FILTER_STATUSES.map(status => {
            const config = BILL_STATUS_CONFIG[status];
            const isActive = statusArray.includes(status);
            return (
              <li key={status}>
                <label className="dropdown-item d-flex align-items-center py-2 mb-0" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" className="form-check-input me-2 mt-0" checked={isActive} onChange={() => toggleStatus(status)} />
                  <span className={`badge ${config.badgeClass} me-2`} style={{ width: 8, height: 8, padding: 0 }} /> {config.label}
                </label>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="d-flex align-items-center gap-2">
        <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
        <span className="text-muted small">to</span>
        <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
        {hasDateRange && <button className="btn btn-sm btn-light" onClick={clearDateRange} title="Clear date range">Clear</button>}
      </div>
      {hasActiveFilters && <button className="btn btn-sm btn-light" onClick={clearFilters}><i className="bi bi-x-lg me-1" /> Clear all</button>}
      <div className="ms-auto">
        <button className="btn btn-sm btn-light" onClick={() => fetchBills()} title="Refresh">
          <i className="bi bi-arrow-clockwise me-1" /> Refresh
        </button>
      </div>
    </div>
  );

  const filterRowMobile = (
    <div className="accounting-filters-row accounting-filters-mobile d-flex align-items-center gap-2">
      <div className="d-flex align-items-center position-relative flex-grow-1 min-w-0">
        <i className="bi bi-search position-absolute ms-3 text-muted" />
        <input type="text" className="form-control form-control-sm ps-10 accounting-search-mobile" placeholder="Search bills, descriptions..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <Dropdown align="end" className="d-flex">
        <Dropdown.Toggle variant="light" size="sm" className="accounting-filter-toggle-mobile d-flex align-items-center gap-2" id="bill-filters-dropdown">
          <i className="bi bi-sliders" />
          <span className="d-none d-sm-inline">Filters</span>
          {hasActiveFilters && <span className="badge bg-primary rounded-pill" style={{ fontSize: '0.65rem' }} />}
        </Dropdown.Toggle>
        <Dropdown.Menu className="accounting-filter-menu-mobile p-0" style={{ minWidth: 'min(320px, 100vw - 2rem)', maxHeight: '80vh', overflowY: 'auto' }}>
          <div className="p-3">
            <div className="dropdown-header text-uppercase fs-8 text-muted mb-2">Status</div>
            <label className="dropdown-item d-flex align-items-center py-3 mb-0 accounting-filter-touch" style={{ cursor: 'pointer' }}>
              <input type="checkbox" className="form-check-input me-2 mt-0" checked={statusArray.length === 0} onChange={() => setBillFilters({ status: undefined })} />
              All Statuses
            </label>
            {FILTER_STATUSES.map(status => {
              const config = BILL_STATUS_CONFIG[status];
              const isActive = statusArray.includes(status);
              return (
                <label key={status} className="dropdown-item d-flex align-items-center py-3 mb-0 accounting-filter-touch" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" className="form-check-input me-2 mt-0" checked={isActive} onChange={() => toggleStatus(status)} />
                  <span className={`badge ${config.badgeClass} me-2`} style={{ width: 8, height: 8, padding: 0 }} /> {config.label}
                </label>
              );
            })}
            <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Date range</div>
            <div className="d-flex align-items-center gap-2 flex-wrap px-0 pb-2">
              <input type="date" className="form-control form-control-sm accounting-input-touch" style={{ flex: '1 1 120px', minWidth: 0 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
              <span className="text-muted small">to</span>
              <input type="date" className="form-control form-control-sm accounting-input-touch" style={{ flex: '1 1 120px', minWidth: 0 }} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
              {hasDateRange && <button type="button" className="btn btn-sm btn-light accounting-btn-touch" onClick={clearDateRange}>Clear dates</button>}
            </div>
            {hasActiveFilters && (
              <button type="button" className="btn btn-sm btn-light w-100 mt-2 accounting-btn-touch" onClick={clearFilters}><i className="bi bi-x-lg me-2" /> Clear all filters</button>
            )}
            <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Actions</div>
            <button type="button" className="dropdown-item d-flex align-items-center py-3 accounting-filter-touch" onClick={() => fetchBills()}>
              <i className="bi bi-arrow-clockwise text-primary me-3" /> Refresh
            </button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const filterRow = isMobile ? filterRowMobile : filterRowDesktop;

  if (embedded) {
    return <div className="py-3 px-4 px-md-5 border-bottom accounting-filter-embedded-wrap">{filterRow}</div>;
  }
  return (
    <div className="card mb-5">
      <div className="card-body py-3">
        {filterRow}
      </div>
    </div>
  );
}

export default BillFilters;
